import { useState, useCallback, useEffect, useRef } from "react";

import Hls from "hls.js";
import { compact, isEmpty } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import VideoSelectionOverlay from "@app/analytics/MeetingReport/components/VideoSelectionOverlay";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import { useMeetingReportContext } from "@app/analytics/MeetingReport/providers/MeetingReportContext";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import { VideoReel } from "@app/analytics/MeetingReport/types";
import {
  getDurationFromClips,
  getParagraphAtTimestamp,
} from "@app/analytics/MeetingReport/utils";
import VideoAnnotationOverlay from "@app/analytics/MeetingReport/VideoPlayer/VideoAnnotationOverlay";
import { UploadFileType, VideoRecording } from "@app/common/types";

import Stack from "@rui/Stack";

import HighlightReelSwitch from "./HighlightReelSwitch";
import { VideoContainer, StyledVideo } from "./styles";
import useVideoControls from "./useVideoControls";
import VideoControls from "./VideoControls";
import VideoMetrics from "./VideoMetrics";
import WaveGif from "../assets/audioWave.gif";

const HLS_CONFIG = {
  autoStartLoad: true,
  startPosition: 0,
  capLevelToPlayerSize: true,
};

const getXhrSetup = (recordingToken: string) => (xhr: XMLHttpRequest) => {
  xhr.setRequestHeader("Authorization", `Bearer ${recordingToken}`);
};

const attachMp4ToVideo = (mp4Url: string | null, video: HTMLVideoElement) => {
  // first, remove any previously-attached source tags from the video
  const existingSourceChildren = Array.from(
    video.getElementsByTagName("source")
  );
  existingSourceChildren.forEach((child) => video.removeChild(child));

  if (mp4Url) {
    // next, attach a new source element if the url is non-null
    const sourceElement = document.createElement("source");
    sourceElement.src = mp4Url;
    sourceElement.type = "video/mp4";
    video.appendChild(sourceElement);
  }
};

/**
 * find the next clip index, assumes clips are sorted by time
 */
const findNextClipIndex = (videoReel: VideoReel, timestamp: number) => {
  const { clips } = videoReel;
  return clips.findIndex((clip) => timestamp < clip.startTimestamp);
};

const Video = ({
  autoPlay,
  hasHighlightReel,
  hasTrailer,
  isLoading,
  thumbnailUrl,
  videoRecording,
  uploadFileType,
}: {
  autoPlay: boolean;
  hasHighlightReel: boolean;
  hasTrailer: boolean;
  isLoading: boolean;
  thumbnailUrl?: string | null;
  videoRecording: VideoRecording;
  uploadFileType?: UploadFileType;
}) => {
  const { playlistUrl: src, recordingAccessToken: token } = videoRecording;
  const [queryParams] = useSearchParams();

  const [hasPlayedAnyVideo, setHasPlayedAnyVideo] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const hls = useRef<Hls | null>(null);
  const { turns } = useTranscript();

  const initStartTime = queryParams.get("t")
    ? parseFloat(queryParams.get("t") as string)
    : undefined;

  const setPlaybackParagraphId = useTranscriptStore(
    (state) => state.setPlaybackParagraphId
  );

  const {
    currentTimestamp,
    setCurrentTimestamp,
    highlightReel,
    videoReel,
    setVideoReel,
    videoReelClipIndex,
    setVideoReelClipIndex,
    videoStartAt,
    setVideoStartAt,
    videoStartTimeOffset,
    watchedHighlights,
    setWatchedHighlights,
    paused,
    setWatchedTrailer,
    startHighlightReel,
    startRecording,
    startTrailer,
    trailer,
  } = useVideoStore((state) => ({
    currentTimestamp: state.currentTimestamp,
    setCurrentTimestamp: state.setCurrentTimestamp,
    highlightReel: state.highlightReel,
    videoReel: state.videoReel,
    setVideoReel: state.setVideoReel,
    videoReelClipIndex: state.videoReelClipIndex,
    setVideoReelClipIndex: state.setVideoReelClipIndex,
    videoStartAt: state.videoStartAt,
    setVideoStartAt: state.setVideoStartAt,
    videoStartTimeOffset: state.videoStartTimeOffset,
    watchedHighlights: state.watchedHighlights,
    setWatchedHighlights: state.setWatchedHighlights,
    paused: state.paused,
    setWatchedTrailer: state.setWatchedTrailer,
    startHighlightReel: state.startHighlightReel,
    startRecording: state.startRecording,
    startTrailer: state.startTrailer,
    trailer: state.trailer,
  }));

  const { video, setVideo } = useMeetingReportContext();
  const { mute, play, pause } = useVideoControls();

  const handleTimeUpdate = useCallback(() => {
    if (!video) return;

    if (videoReel?.playing) {
      const currentClip = videoReel.clips[videoReelClipIndex ?? 0];

      const nextClipIndex =
        videoReelClipIndex === null
          ? findNextClipIndex(videoReel, video.currentTime)
          : videoReelClipIndex + 1;

      const nextClip = videoReel.clips[nextClipIndex];

      if (!nextClip && video.currentTime >= currentClip.endTimestamp) {
        // If no more clips and done with the current clip, pause
        pause();

        if (videoReel.name === "highlights") {
          // if this is the highlight reel, mark the highlight reel as viewed
          setWatchedHighlights(true);
        }
      } else if (
        (nextClip &&
          video.currentTime >= nextClip.startTimestamp &&
          nextClipIndex !== videoReelClipIndex) ||
        video.currentTime >= currentClip.endTimestamp
      ) {
        // update the clip index to the next clip if we are
        // past the start of the next clip, or past the end of the current clip
        setVideoReelClipIndex(nextClipIndex);
      }
    }

    setCurrentTimestamp(video.currentTime);
    if (video.currentTime >= video.duration) {
      setPlaybackParagraphId(null);
      return;
    }

    const roundedTimestamp = Math.round(video.currentTime);

    // ignoring 0 timestamp prevents the first transcript turn from being
    // awkwardly/perpetually highlighted when the video hasn't even started
    // playing yet; don't do anything w/ auto-follow while the trailer is
    // playing since the timestamps won't match up with turns
    if (video.currentTime === 0 || trailer.active) return;

    const rawTimestamp =
      videoRecording?.startTime
        .plus({ seconds: roundedTimestamp })
        .toMillis() ?? 0;

    const paragraph = getParagraphAtTimestamp(turns, rawTimestamp);

    // double-check the timestamp, this prevents the first turn from being highlighted
    // when it may not actually start until a minute or so into the video
    if (paragraph && paragraph.rawStartTime.toMillis() <= rawTimestamp) {
      setPlaybackParagraphId(paragraph.id);
    }
  }, [
    video,
    turns.length,
    videoReel,
    videoReelClipIndex,
    videoStartTimeOffset,
    trailer.active,
  ]);

  useEffect(() => {
    setVideo(videoRef.current);
  }, [videoRef.current]);

  const shouldPlayFromTimestamp = !hasPlayedAnyVideo && Boolean(initStartTime);

  useEffect(() => {
    if (!shouldPlayFromTimestamp) return;
    startRecording(initStartTime);
  }, [shouldPlayFromTimestamp]);

  const shouldAttachTrailer =
    hasTrailer && trailer.active && !shouldPlayFromTimestamp;
  const shouldAutoStartVideo =
    !isLoading && Boolean(autoPlay) && !hasPlayedAnyVideo;

  const handleAutoPlay = (type: "trailer" | "highlight_reel" | "recording") => {
    if (!shouldAutoStartVideo || hasPlayedAnyVideo || !video?.paused) return;

    // need to mute if autoplaying before user has interacted with the page
    mute();
    setHasPlayedAnyVideo(true);

    if (type === "highlight_reel") {
      startHighlightReel();
    } else {
      play();
    }
  };

  useEffect(() => {
    if (!video) return;
    if (hls.current) hls.current.destroy();

    if (shouldAttachTrailer) {
      attachMp4ToVideo(trailer.url, video);
      if (shouldAutoStartVideo) {
        handleAutoPlay("trailer");
      } else if (trailer.autoStart) {
        play();
      }
    } else {
      hls.current = new Hls({
        ...HLS_CONFIG,
        xhrSetup: getXhrSetup(token),
        startPosition: initStartTime,
      });

      // load stream
      hls.current.loadSource(src);

      // bind instances together
      hls.current.attachMedia(video);

      if (shouldAutoStartVideo && !hasTrailer) {
        hls.current.on(Hls.Events.MEDIA_ATTACHED, () => {
          if (hasHighlightReel) {
            if (!isEmpty(highlightReel?.clips)) {
              handleAutoPlay("highlight_reel");
            }
          } else {
            handleAutoPlay("recording");
          }
        });
      } else {
        // ensure video is paused. if the video is already paused, this is a
        // noop update. but call anyway since the video's paused state can
        // get out of sync when switching between video sources.
        pause();
      }
    }
  }, [
    video,
    shouldAttachTrailer,
    hasTrailer,
    trailer.url,
    hasHighlightReel,
    highlightReel?.clips.length,
  ]);

  useEffect(() => {
    if (!video) return;

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [handleTimeUpdate]);

  useEffect(() => {
    if (!video || videoStartAt == null) return;
    video.currentTime = videoStartAt;

    if (video.paused) play();
    setVideoReelClipIndex(null);
    setVideoReel(null);
    setVideoStartAt(null);
  }, [video, videoStartAt, videoReel]);

  useEffect(() => {
    if (!video || !videoReel) return;
    // go back to beginning if past the last clip
    const clipIndex = Math.max(
      findNextClipIndex(videoReel, video.currentTime),
      0
    );

    setVideoReelClipIndex(clipIndex);
    const clipStartTimestamp = videoReel.clips[clipIndex]?.startTimestamp;

    if (clipStartTimestamp) {
      video.currentTime = clipStartTimestamp;
    }
    if (video.paused) play();
  }, [videoReel]);

  // Update the video time if the clip index changes,
  // and we aren't already in the middle of the new clip
  // (clips can overlap)
  useEffect(() => {
    if (!video || !videoReel || videoReelClipIndex == null) return;
    const clip = videoReel.clips[videoReelClipIndex];
    if (!clip) return;

    if (
      video.currentTime < clip.startTimestamp ||
      video.currentTime > clip.endTimestamp
    ) {
      video.currentTime = clip.startTimestamp;
    }
    if (video.paused) play();
  }, [videoReelClipIndex]);

  const currentVideoDuration =
    video && !isNaN(video.duration) ? video.duration : 0;
  const currentVideoIsOver =
    currentVideoDuration > 0 && currentTimestamp >= currentVideoDuration;

  useEffect(() => {
    if (trailer.active && currentVideoIsOver) {
      setWatchedTrailer(true);
    }
  }, [trailer.active, currentVideoIsOver]);

  const handleVideoClick = () => {
    if (!video) return;
    return video.paused ? play() : pause();
  };

  // fall back to video's duration if videoDuration is not returned from recordings endpoint
  // (will be the case for older meetings), and the trailer isn't the active video
  const fullRecordingDuration =
    videoRecording.videoDuration ?? (trailer.active ? 0 : currentVideoDuration);

  const handleSelectTrailer = () => {
    setHasPlayedAnyVideo(true);
    if (trailer.active) {
      play();
    } else {
      Events.playTrailer();
      startTrailer();
    }
  };

  const handleSelectHighlights = () => {
    setHasPlayedAnyVideo(true);
    if (watchingHighlights) {
      play();
    } else {
      Events.playHighlightReel({ source: "button" });
      startHighlightReel();
    }
  };

  const handleSelectRecording = () => {
    setHasPlayedAnyVideo(true);
    if (watchingRecording) {
      play();
    } else {
      Events.playVideo();
      startRecording(hasPlayedAnyVideo ? undefined : initStartTime);
    }
  };

  const showVideoSelectionOverlay = paused || currentVideoIsOver;

  const videoInProgress = currentTimestamp > 0;
  const watchingTrailer = trailer.active && videoInProgress;
  const watchingHighlights =
    !watchingTrailer && videoReel?.name === "highlights" && videoInProgress;
  const watchingRecording =
    !watchingTrailer && !watchingHighlights && videoInProgress;

  // spotlight the trailer by default, assuming there is a trailer to play
  const trailerIsPrimary = shouldAttachTrailer;

  // spotlight the highlights by default if the user already viewed the trailer, has not
  // yet viewed the highlights, and is not in the middle of the recording
  const highlightsIsPrimary =
    watchingHighlights ||
    (trailer.watched && !watchedHighlights && !watchingRecording);

  // spotlight the recording if the user is in the middle of it, or if neither the trailer
  // nor the highlights are being spotlighted
  const recordingIsPrimary =
    watchingRecording || (!trailerIsPrimary && !highlightsIsPrimary);

  const remainingHighlightDuration = getDurationFromClips(
    videoReel?.clips ?? [],
    currentTimestamp
  );

  const trailerOverlayOption = {
    label: watchingTrailer
      ? "Resume"
      : trailer.watched
        ? "Replay Trailer"
        : "Trailer",
    duration: {
      total: 0.9, // durations here are placeholders, trailers shouldn't be longer than 1 minute
      remaining: watchingTrailer ? 0.9 : undefined,
      unit: "minutes",
    },
    icon: "🎬",
    primary: trailerIsPrimary,
    onClick: handleSelectTrailer,
  };

  const highlightsOverlayOption = {
    label: watchingHighlights
      ? "Resume"
      : watchedHighlights
        ? "Replay Highlights"
        : "Highlights",
    duration: {
      total: highlightReel?.duration ?? 0,
      remaining: watchingHighlights ? remainingHighlightDuration : undefined,
    },
    icon: "⚡️",
    primary: highlightsIsPrimary,
    onClick: handleSelectHighlights,
  };

  const recordingOverlayOption =
    !hasPlayedAnyVideo && Boolean(initStartTime)
      ? {
          label: "Play from",
          timestamp: initStartTime,
          primary: recordingIsPrimary,
          icon: "⏱",
          onClick: handleSelectRecording,
        }
      : {
          label: watchingRecording ? "Resume" : "Recording",
          duration: {
            total: fullRecordingDuration,
            remaining: watchingRecording
              ? Math.max(fullRecordingDuration - currentTimestamp, 0)
              : undefined,
          },
          primary: recordingIsPrimary,
          icon: "⏱",
          onClick: handleSelectRecording,
        };

  return (
    <Stack sx={{ width: "100%", zIndex: 4 }}>
      <VideoContainer className="meeting-report__video-container">
        <StyledVideo
          ref={videoRef}
          poster={thumbnailUrl ?? ""}
          onClick={handleVideoClick}
          id="mr-video"
          preload="auto"
        />
        {showVideoSelectionOverlay ? (
          <VideoSelectionOverlay
            options={compact([
              trailer.url ? trailerOverlayOption : null,
              isEmpty(highlightReel?.clips) ? null : highlightsOverlayOption,
              recordingOverlayOption,
            ])}
          />
        ) : (
          <>
            <VideoAnnotationOverlay />
            <VideoMetrics />
          </>
        )}
        {uploadFileType &&
          uploadFileType === "audio" &&
          !showVideoSelectionOverlay && (
            <img
              src={WaveGif}
              style={{
                position: "absolute",
                height: "70px",
                top: "35%",
                left: "43%",
              }}
            />
          )}
        <VideoControls
          currentTimestamp={currentTimestamp}
          duration={
            trailerIsPrimary ? currentVideoDuration : fullRecordingDuration
          }
          hideAnnotations={trailerIsPrimary}
        />
      </VideoContainer>
      <HighlightReelSwitch />
    </Stack>
  );
};

export default Video;
